import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { SplitButton } from "primereact/splitbutton";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  activateCatBatch,
  catResults,
  runCatModel,
  rmsEltJobHistory,
  downloadAllRmsEltArtifacts,
  rerunCatFinancials,
  resumeCatPipeline,
  type EltPipelineType,
  type RerunFinancialsCatType,
  type CatVendorType,
  icmYltJobHistory,
  downloadAllIcmYltArtifacts,
  spatialKatJobHistory,
  downloadAllSpatialKatArtifacts,
} from "../../../api/routes";
import { useCatModelPolling } from "./useCatModelPolling";
import { useCatRunStatusPolling } from "./useCatRunStatusPolling";
import { SafeReportResponse } from "../Conversion";
import styles from "./CatCard.module.css";
import {
  WorkStatus,
  CatResultsResponse,
  CatJobHistoryItem,
  CatJobHistoryResponse,
  CatPrecheckResponse,
  CatRunChangeResult,
  CatRunRequest,
  CatRunResult,
  CatRunSpec,
  EqbdRaterRunSpec,
  RaterRunSpec,
  VendorPrecheck,
} from "../../../client/types.gen";
import { useToast } from "../../../context-providers/Toast";
import { downloadBlob } from "../../../util";
import { apiErrorDetail } from "../../../api/errors";
import { useFeatureEnabled } from "../../../IfFeatureEnabled";
import {
  invalidateJobState,
  useCatVendorEnrichment,
  useRunModeling,
  useLocalUser,
  usePrecheck,
} from "../data";
import { reportKeys } from "../queryKeys";
import {
  CAT_VENDORS,
  CatRunMode,
  ENRICHMENT_SOURCE_OPTIONS,
  EnrichmentChoice,
  EnrichmentSource,
  EnrichmentVendor,
  ModelTabOption,
  SelectedModel,
  fromEnrichmentChoice,
  getPerilDisplayName,
  selectedModelToAuditSource,
  toEnrichmentChoice,
} from "./types";
import { useIsMobile } from "../../../responsive/useMediaQuery";
import { RaterTab } from "./RaterTab";
import { EqbdRaterTab } from "./EqbdRaterTab";
import { SindriReplacementCostTab } from "./SindriReplacementCostTab";
import { useReportJobs } from "../rater/useIntegrationJobs";
import { CatModelSwitcher, ModelTabStatus } from "./CatModelSwitcher";
import { CatResultsView } from "./CatResultsView";
import { EnrichmentBadge } from "./EnrichmentBadge";
import { ModelRunComparisonDialog } from "./ModelRunComparisonDialog";
import {
  CatRunStatusIndicator,
  CatStatusBanner,
  StatusRow,
} from "./CatRunStatusIndicator";
import { TemplateButton } from "../template/TemplateButton";
import { ParsedTemplate } from "../template/data";

interface CatCardProps {
  specialtyPropertyInfo: SafeReportResponse;
}

const LARGE_CAT_JOB_THRESHOLD = 10_000;

// A single-model action (its own Run button, a shadow run) starts just that
// model's run.
const runOne = async (
  reportId: string,
  spec: CatRunRequest["runs"][number]
) => {
  const [outcome] = (await runCatModel(reportId, [spec])).runs;
  if (!outcome) throw new Error(`No outcome for the ${spec.type} run`);
  return outcome;
};

// A shadow started from a vendor's Shadow run button is an engineer's test
// run; everything else, including the Run Modeling dialog, is an underwriting
// run.
const baselineSpec = (
  type: CatVendorType,
  activate: boolean,
  enrichmentSource: EnrichmentSource,
  mode?: CatRunMode
): CatRunSpec => ({
  type,
  activate,
  enrichment_source: enrichmentSource,
  mode: mode ?? "default",
  run_type: activate ? "underwriting" : "test_run",
});

// The vendor data a history row's run modeled with. A legacy enriched run with
// no recorded source predates Cape, so it reads as Nearmap (as its badge does).
const jobEnrichmentSource = (job: CatJobHistoryItem): EnrichmentSource =>
  job.enrichment_source === "cape"
    ? "cape"
    : job.enrichment_source === "nearmap" || job.enriched
      ? "nearmap"
      : null;

// Each vendor's run history comes from its own endpoint, so refreshing it
// after acting on a row is dispatched per model.
const JOB_HISTORY_FETCHER: Record<
  SelectedModel,
  (reportId: string) => Promise<CatJobHistoryResponse>
> = {
  rms_elt: rmsEltJobHistory,
  icm_ylt: icmYltJobHistory,
  spatialkat: spatialKatJobHistory,
};

export const CatCard: React.FC<CatCardProps> = ({ specialtyPropertyInfo }) => {
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  // What each vendor's tab is currently showing: the report's active results
  // until a run history row is opened, then that batch's. Every slot holds a
  // whole response but only its own key is ever read, so the tabs stay
  // independent — opening a batch on one leaves the others alone.
  const [resultsByVendor, setResultsByVendor] = useState<
    Record<SelectedModel, CatResultsResponse | null>
  >({ rms_elt: null, icm_ylt: null, spatialkat: null });
  const setVendorResults = (
    vendor: SelectedModel,
    results: CatResultsResponse
  ) => setResultsByVendor((prev) => ({ ...prev, [vendor]: results }));
  const [jobHistory, setJobHistory] = useState<CatJobHistoryItem[]>([]);
  // Per vendor, the history batch its tabs show; null is the active run.
  const [viewedBatchByVendor, setViewedBatchByVendor] = useState<
    Record<SelectedModel, string | null>
  >({ rms_elt: null, icm_ylt: null, spatialkat: null });
  const setViewedBatch = (vendor: SelectedModel, batchId: string | null) =>
    setViewedBatchByVendor((prev) => ({ ...prev, [vendor]: batchId }));
  // Per vendor, further completed batches set beside the viewed run on the
  // By Layer and Peril tab.
  const [compareBatchesByVendor, setCompareBatchesByVendor] = useState<
    Record<SelectedModel, string[]>
  >({ rms_elt: [], icm_ylt: [], spatialkat: [] });
  const [compareBaseJob, setCompareBaseJob] =
    useState<CatJobHistoryItem | null>(null);
  const [searchParams] = useSearchParams();
  const [selectedModel, setSelectedModel] = useState<ModelTabOption>(
    searchParams.get("model") === "rater" ? "rater" : "rms_elt"
  );
  const [showAllLayers, setShowAllLayers] = useState(false);
  const isMobile = useIsMobile();
  const toast = useToast();
  const queryClient = useQueryClient();
  const user = useLocalUser();
  const catModelOverrideAllowed = useFeatureEnabled(
    "cat-model-override-allowed",
    undefined,
    user
  );
  const largeCatJobsAllowed = useFeatureEnabled(
    "large-cat-jobs-allowed",
    undefined,
    user
  );
  const rmsRerunFinancialsEnabled = useFeatureEnabled(
    "rms-rerun-financials",
    undefined,
    user
  );
  const catShadowRunAllowed = useFeatureEnabled(
    "cat-shadow-run",
    undefined,
    user
  );
  const catStalenessUiEnabled = useFeatureEnabled(
    "cat-staleness-ui",
    undefined,
    user
  );
  const catRunModeOverrideAllowed = useFeatureEnabled(
    "cat-run-mode-override",
    undefined,
    user
  );
  const catActivateRunAllowed = useFeatureEnabled(
    "cat-activate-run",
    undefined,
    user
  );
  const sindriRunAllowed = useFeatureEnabled(
    "sindri-replacement-cost-run",
    undefined,
    user
  );
  // The batch whose resume / re-run-financials is currently being kicked
  // off, so the history row can show a spinner and the rest disable.
  const [recoveryInFlightBatchId, setRecoveryInFlightBatchId] = useState<
    string | null
  >(null);
  const [activatingBatchId, setActivatingBatchId] = useState<string | null>(
    null
  );
  const numLocations = specialtyPropertyInfo.precomputed_metadata.num_locations;
  const isLargeJob = numLocations > LARGE_CAT_JOB_THRESHOLD;
  const [showLargeJobBlockedDialog, setShowLargeJobBlockedDialog] =
    useState(false);
  const [showRunModelingDialog, setShowRunModelingDialog] = useState(false);
  const [runModelingRms, setRunModelingRms] = useState(true);
  const [runModelingKatRisk, setRunModelingKatRisk] = useState(true);
  const [runModelingIcm, setRunModelingIcm] = useState(true);
  const [runModelingRater, setRunModelingRater] = useState(true);
  const [runModelingEqbd, setRunModelingEqbd] = useState(true);
  const [raterTemplateOverride, setRaterTemplateOverride] = useState<
    ParsedTemplate | undefined
  >(undefined);
  // The vendor data each vendor's live run modeled with. A vendor's own Run
  // and Shadow run buttons re-run it under that; the Run Modeling dialog
  // picks its own.
  const { data: vendorEnrichment } = useCatVendorEnrichment(
    specialtyPropertyInfo.report_id
  );
  const liveVendor = (vendor: EnrichmentVendor) =>
    vendorEnrichment?.vendors.find((v) => v.vendor === vendor);
  const liveSourceFor = (vendor: EnrichmentVendor): EnrichmentSource =>
    liveVendor(vendor)?.live_source ?? null;
  // Until the live runs are known, a vendor's buttons would re-run it on
  // broker data and replace enhanced results, so they wait.
  const liveRunsUnknown = vendorEnrichment === undefined;
  // The dialog opens on the vendor data the live CAT runs share, or broker
  // data when they disagree or there are none.
  const defaultRunChoice = (): EnrichmentChoice => {
    const live = new Set(
      CAT_VENDORS.flatMap((vendor) => {
        const v = liveVendor(vendor);
        return v?.has_live_run ? [toEnrichmentChoice(v.live_source)] : [];
      })
    );
    const [only] = live;
    return live.size === 1 && only !== undefined ? only : "broker";
  };
  const [runModelingEnrichment, setRunModelingEnrichment] =
    useState<EnrichmentChoice>("broker");
  const baselineRun =
    (type: CatVendorType) =>
    (reportId: string, activate: boolean, mode?: CatRunMode) =>
      runOne(reportId, baselineSpec(type, activate, liveSourceFor(type), mode));
  const guardLargeJob = (action: () => void) => {
    if (isLargeJob && !largeCatJobsAllowed) {
      setShowLargeJobBlockedDialog(true);
      return;
    }
    action();
  };
  const reportId = specialtyPropertyInfo.report_id;

  const handleJobHistoryUpdate = useCallback(
    (modelType: string, jobs: CatJobHistoryItem[]) => {
      setJobHistory((prev) => {
        const otherJobs = prev.filter((j) => j.type !== modelType);
        return [...otherJobs, ...jobs].sort(
          (a, b) =>
            new Date(b.started_at).getTime() - new Date(a.started_at).getTime()
        );
      });
    },
    []
  );

  const {
    data: precheckData,
    isFetching: precheckFetching,
    isError: precheckError,
    refetch: refetchPrecheck,
  } = usePrecheck(reportId);

  const statusPolling = useCatRunStatusPolling();

  const rmsEltPolling = useCatModelPolling({
    modelType: "rms_elt",
    displayName: "RMS",
    reportId,
    toast,
    statusPolling,
    initiateRequest: baselineRun("rms_elt"),
    jobHistory: { fetch: rmsEltJobHistory, onUpdate: handleJobHistoryUpdate },
    onComplete: async () => {
      setVendorResults("rms_elt", await catResults(reportId));
      setViewedBatch("rms_elt", null);
      refetchPrecheck();
      refreshVendorEnrichment();
    },
  });

  const icmYltPolling = useCatModelPolling({
    modelType: "icm_ylt",
    displayName: "ICM",
    reportId,
    toast,
    statusPolling,
    initiateRequest: baselineRun("icm_ylt"),
    jobHistory: { fetch: icmYltJobHistory, onUpdate: handleJobHistoryUpdate },
    onComplete: async () => {
      setVendorResults("icm_ylt", await catResults(reportId));
      setViewedBatch("icm_ylt", null);
      refetchPrecheck();
      refreshVendorEnrichment();
    },
  });

  const spatialKatPolling = useCatModelPolling({
    modelType: "spatialkat",
    displayName: "KatRisk",
    reportId,
    toast,
    statusPolling,
    initiateRequest: baselineRun("spatialkat"),
    jobHistory: {
      fetch: spatialKatJobHistory,
      onUpdate: handleJobHistoryUpdate,
    },
    onComplete: async () => {
      setVendorResults("spatialkat", await catResults(reportId));
      setViewedBatch("spatialkat", null);
      refetchPrecheck();
      refreshVendorEnrichment();
    },
    showErrorDetail: true,
  });

  const precheck = precheckData?.cat;
  const runStatusFor = (
    isRunning: boolean,
    result: CatRunChangeResult | undefined
  ): ModelTabStatus | undefined =>
    isRunning ? "running" : precheckFetching ? "loading" : result;

  // Excludes the rater tabs, which have no staleness concept.
  const runStatusByModel:
    | Partial<Record<ModelTabOption, ModelTabStatus>>
    | undefined = catStalenessUiEnabled
    ? {
        rms_elt: runStatusFor(
          rmsEltPolling.isPending,
          precheck?.rms.results_change
        ),
        spatialkat: runStatusFor(
          spatialKatPolling.isPending,
          precheck?.spatialkat.results_change
        ),
        icm_ylt: runStatusFor(
          icmYltPolling.isPending,
          precheck?.icm.results_change
        ),
      }
    : undefined;
  const ratePrecheck = precheckData?.rater;
  const { jobs: raterJobs } = useReportJobs(reportId, {
    integrationTypes: ["RATER"],
  });
  const raterBlocked =
    !ratePrecheck || ratePrecheck.warnings.some((w) => w.blocking);
  const hasRaterResults = raterJobs.some((j) => j.outcome === "success");
  // The rater tabs read their results off the report's job list.
  const refreshJobState = async () => {
    await invalidateJobState(queryClient, reportId);
  };
  // A completed or promoted run may be its vendor's new live run.
  const refreshVendorEnrichment = () =>
    queryClient.invalidateQueries({
      queryKey: reportKeys.vendorEnrichment(reportId),
    });
  const startRater = async (spec: RaterRunSpec | EqbdRaterRunSpec) => {
    const outcome = await runOne(reportId, spec);
    await refreshJobState();
    return outcome;
  };
  const raterPolling = useCatModelPolling({
    modelType: "rater",
    displayName: "Rater",
    reportId,
    toast,
    statusPolling,
    initiateRequest: () =>
      startRater({
        type: "rater",
        template_override_id: raterTemplateOverride?.template_id ?? null,
        enrichment_source: liveSourceFor("rater"),
      }),
    onComplete: refreshJobState,
    showErrorDetail: true,
  });

  const { jobs: eqbdRaterJobs } = useReportJobs(reportId, {
    integrationTypes: ["EQBD_RATER"],
  });
  const hasEqbdResults = eqbdRaterJobs.some((j) => j.outcome === "success");
  const eqbdRaterPolling = useCatModelPolling({
    modelType: "eqbd_rater",
    displayName: "EB Rater",
    reportId,
    toast,
    statusPolling,
    initiateRequest: () =>
      startRater({
        type: "eqbd_rater",
        enrichment_source: liveSourceFor("eqbd_rater"),
      }),
    onComplete: refreshJobState,
    showErrorDetail: true,
  });
  // Also counts rater runs this page didn't start, which it doesn't poll.
  const raterPending =
    raterPolling.isPending || raterJobs.some((j) => !j.outcome);
  const eqbdRaterPending =
    eqbdRaterPolling.isPending || eqbdRaterJobs.some((j) => !j.outcome);
  const runModeling = useRunModeling(reportId);

  // Load existing results and check for pending jobs on mount
  useEffect(() => {
    const loadExistingResults = async () => {
      try {
        const [catRes, rmsEltHist, icmYltHist, skHist] = await Promise.all([
          catResults(specialtyPropertyInfo.report_id).catch(() => null),
          rmsEltJobHistory(specialtyPropertyInfo.report_id),
          icmYltJobHistory(specialtyPropertyInfo.report_id).catch(
            () => ({ jobs: [] }) as CatJobHistoryResponse
          ),
          spatialKatJobHistory(specialtyPropertyInfo.report_id),
        ]);

        // A vendor with no modeled layers stays null so its tab shows the
        // empty state rather than an empty results table.
        if (catRes) {
          setResultsByVendor({
            rms_elt: catRes.rms_elt?.aggregate?.layers?.length ? catRes : null,
            icm_ylt: catRes.icm_ylt?.aggregate?.layers?.length ? catRes : null,
            spatialkat: catRes.spatialkat?.aggregate?.layers?.length
              ? catRes
              : null,
          });
        }

        // Combine job histories
        const allJobs = [
          ...(rmsEltHist.jobs ?? []),
          ...(icmYltHist.jobs ?? []),
          ...(skHist.jobs ?? []),
        ].sort(
          (a, b) =>
            new Date(b.started_at).getTime() - new Date(a.started_at).getTime()
        );

        setJobHistory(allJobs);

        // Resume polling for any active jobs. Clearance auto-runs are excluded:
        // polling them would spin the Run buttons and announce a completion for
        // modeling the underwriter never started. They stay listed in Run
        // History, which is where they are meant to be seen.
        const getActiveIds = (hist: { jobs?: CatJobHistoryItem[] | null }) =>
          (hist.jobs ?? [])
            .filter(
              (job) =>
                !job.auto_run &&
                (job.status === WorkStatus.PENDING ||
                  job.status === WorkStatus.IN_PROGRESS)
            )
            .map((job) => job.batch_id);

        const activeRmsEltIds = getActiveIds(rmsEltHist);
        const activeIcmYltIds = getActiveIds(icmYltHist);
        const activeSkIds = getActiveIds(skHist);

        if (activeRmsEltIds.length > 0) {
          rmsEltPolling.setPendingRequestIds(activeRmsEltIds);
        }
        if (activeIcmYltIds.length > 0) {
          icmYltPolling.setPendingRequestIds(activeIcmYltIds);
        }
        if (activeSkIds.length > 0) {
          spatialKatPolling.setPendingRequestIds(activeSkIds);
        }
      } catch (error) {
        console.log("No existing CAT results found");
      } finally {
        setIsInitialLoading(false);
      }
    };
    loadExistingResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-existing, not yet audited
  }, [specialtyPropertyInfo.report_id]);

  const rmsNeedsConfirmation =
    (precheck?.rms.warnings.length ?? 0) > 0 && !precheck?.rms.blocked;

  // Which model's runs can be promoted to the report's live results — every cat
  // vendor with a run history. The rater tabs have no vendor results to promote.
  const activationModel: SelectedModel | null =
    selectedModel === "rms_elt" ||
    selectedModel === "icm_ylt" ||
    selectedModel === "spatialkat"
      ? selectedModel
      : null;

  // Per-row recovery in the Run History tab: each row resumes (failed) or
  // re-runs financials (done) against its own batch. Two gates, one per
  // endpoint, each typed off its own generated union — they accept the same
  // vendors today, and tsc catches it at the call site if either narrows.
  const resumableModel: EltPipelineType | null =
    rmsRerunFinancialsEnabled &&
    (selectedModel === "rms_elt" ||
      selectedModel === "icm_ylt" ||
      selectedModel === "spatialkat")
      ? selectedModel
      : null;
  const rerunFinancialsModel: RerunFinancialsCatType | null =
    rmsRerunFinancialsEnabled &&
    (selectedModel === "rms_elt" ||
      selectedModel === "icm_ylt" ||
      selectedModel === "spatialkat")
      ? selectedModel
      : null;
  const pollingByVendor: Record<
    SelectedModel,
    ReturnType<typeof useCatModelPolling>
  > = {
    rms_elt: rmsEltPolling,
    icm_ylt: icmYltPolling,
    spatialkat: spatialKatPolling,
  };
  // Both are only read once a recovery model is set; the fallback just keeps
  // them defined for the null case.
  const eltPolling = pollingByVendor[rerunFinancialsModel ?? "rms_elt"];
  const eltJobHistory = JOB_HISTORY_FETCHER[rerunFinancialsModel ?? "rms_elt"];

  // The status poll refreshes job history every couple of seconds; this
  // flips the row over without waiting for the next tick. Never throws:
  // a failure here must not read as the action itself having failed.
  const refreshEltJobHistory = async () => {
    if (!rerunFinancialsModel) return;
    try {
      const history = await eltJobHistory(reportId);
      if (history.jobs) {
        handleJobHistoryUpdate(rerunFinancialsModel, history.jobs);
      }
    } catch {
      // the poll picks it up
    }
  };

  const handleRerunCatFinancials = async (
    sourceBatchId: string,
    activate: boolean
  ) => {
    if (!rerunFinancialsModel) return;
    setRecoveryInFlightBatchId(sourceBatchId);
    try {
      const result = await rerunCatFinancials(
        reportId,
        rerunFinancialsModel,
        sourceBatchId,
        activate
      );
      eltPolling.setPendingRequestIds((prev) => [...prev, result.batch_id]);
      toast.show({
        severity: "info",
        summary: activate
          ? "Financials Re-run Started"
          : "Shadow Financials Re-run Started",
        detail: activate
          ? "Re-running the financial model from the selected batch."
          : "Re-running financials without replacing active results.",
      });
      refreshEltJobHistory();
    } catch (error) {
      toast.show({
        severity: "error",
        summary: "Re-run Failed",
        // A batch from before the analysis/financial split has no financial
        // task to reprice and 404s; the server's reason says so, where the
        // generic message leaves the user with no idea why.
        detail:
          apiErrorDetail(error) ?? "Could not start the financials re-run.",
      });
    } finally {
      setRecoveryInFlightBatchId(null);
    }
  };

  const handleResumeCatPipeline = async (
    batchId: string,
    mode: "resume" | "restart"
  ) => {
    if (!resumableModel) return;
    setRecoveryInFlightBatchId(batchId);
    try {
      const result = await resumeCatPipeline(
        reportId,
        resumableModel,
        batchId,
        mode
      );
      eltPolling.setPendingRequestIds((prev) =>
        prev.includes(result.batch_id) ? prev : [...prev, result.batch_id]
      );
      toast.show({
        severity: "info",
        summary: mode === "restart" ? "Run Restarted" : "Run Resumed",
        // SpatialKat doesn't resume in place: the server reprices the stored
        // losses into a new batch, which is the id being polled above.
        detail:
          mode === "restart"
            ? "Restarting modeling with fresh submissions."
            : resumableModel === "spatialkat"
              ? "Re-pricing this run's stored losses into a new batch."
              : "Resuming the selected batch from where it stalled.",
      });
      refreshEltJobHistory();
    } catch (error) {
      toast.show({
        severity: "error",
        summary: mode === "restart" ? "Restart Failed" : "Resume Failed",
        detail:
          apiErrorDetail(error) ??
          (mode === "restart"
            ? "Could not restart the run."
            : "Could not resume the run."),
      });
    } finally {
      setRecoveryInFlightBatchId(null);
    }
  };

  const eltRecovery = rerunFinancialsModel
    ? {
        onResume: (batchId: string) =>
          handleResumeCatPipeline(batchId, "resume"),
        // Restart (drop the stuck ICM request, submit fresh) only makes
        // sense for ICM YLT — the RMS pipelines have no ICM status URL to
        // shed, and the backend rejects restart for them.
        onRestart:
          resumableModel === "icm_ylt"
            ? (batchId: string) => handleResumeCatPipeline(batchId, "restart")
            : undefined,
        onRerunFinancials: handleRerunCatFinancials,
        shadowRerunAllowed: catShadowRunAllowed,
        actionInFlightBatchId: recoveryInFlightBatchId,
        disabled: eltPolling.isPending,
      }
    : null;

  const rmsBlocked = !precheck || precheck.rms.blocked;
  const spatialKatBlocked = !precheck || precheck.spatialkat.blocked;
  const icmYltBlocked = !precheck || precheck.icm.blocked;
  const rmsNotRunnable =
    rmsBlocked || (!catModelOverrideAllowed && rmsNeedsConfirmation);
  const runModelingBlocked =
    !precheck ||
    (rmsNotRunnable && spatialKatBlocked && icmYltBlocked && raterBlocked);
  const blockedModels = precheck
    ? [
        {
          model: "RMS",
          blocked: rmsNotRunnable,
          reasons: precheck.rms.warnings,
        },
        {
          model: "ICM",
          blocked: icmYltBlocked,
          reasons: precheck.icm.warnings,
        },
        {
          model: "KatRisk",
          blocked: spatialKatBlocked,
          reasons: precheck.spatialkat.warnings,
        },
        {
          model: "Rater",
          blocked: raterBlocked,
          reasons: (ratePrecheck?.warnings ?? [])
            .filter((w) => w.blocking)
            .map((w) => w.summary),
        },
      ].filter(({ blocked }) => blocked)
    : [];
  // One row per distinct reason, listing the models it blocks. CAT-wide
  // reasons come first and block every CAT model but not the rater.
  const blockedModelsRows = ((): StatusRow[] => {
    if (precheckError) {
      return [
        {
          summary:
            "Couldn't check whether modeling can run. Refresh the page to try again.",
          details: [],
        },
      ];
    }
    if (!precheck || blockedModels.length === 0) return [];
    const blockedCatModels = blockedModels
      .filter(({ model }) => model !== "Rater")
      .map(({ model }) => model);
    const modelsByReason = new Map<string, string[]>(
      precheck.blocking_reasons.map((reason) => [reason, blockedCatModels])
    );
    for (const { model, reasons } of blockedModels) {
      for (const reason of reasons) {
        modelsByReason.set(reason, [
          ...(modelsByReason.get(reason) ?? []),
          model,
        ]);
      }
    }
    if (modelsByReason.size === 0) {
      return [
        {
          summary: "Some models can't run on this submission.",
          details: blockedModels.map(({ model }) => model),
        },
      ];
    }
    return [...modelsByReason].map(([summary, details]) => ({
      summary,
      details,
    }));
  })();
  const selectedModelBlocked =
    selectedModel === "icm_ylt"
      ? icmYltBlocked
      : selectedModel === "spatialkat"
        ? spatialKatBlocked
        : rmsNotRunnable;
  const openRunModelingDialog = () => {
    setRunModelingRms(!rmsNotRunnable);
    setRunModelingKatRisk(!spatialKatBlocked);
    setRunModelingIcm(!icmYltBlocked);
    setRunModelingRater(!raterBlocked);
    setRunModelingEqbd(!raterBlocked);
    setRunModelingEnrichment(defaultRunChoice());
    setShowRunModelingDialog(true);
  };
  // ``mode`` overrides the ICM/RMS/SpatialKat baseline run behavior (full
  // remodel / finance-only). It only reaches the server for ENG (the override
  // menu is ENG-gated).
  const handleRunModeling = async (mode?: CatRunMode) => {
    if (runModelingRms && isLargeJob && !largeCatJobsAllowed) {
      setShowRunModelingDialog(false);
      setShowLargeJobBlockedDialog(true);
      return;
    }
    setShowRunModelingDialog(false);
    const source = fromEnrichmentChoice(runModelingEnrichment);
    const runs: CatRunRequest["runs"] = [];
    if (runModelingRms && !rmsNotRunnable) {
      runs.push(baselineSpec("rms_elt", true, source, mode));
    }
    if (runModelingKatRisk && !spatialKatBlocked) {
      runs.push(baselineSpec("spatialkat", true, source, mode));
    }
    if (runModelingIcm && !icmYltBlocked) {
      runs.push(baselineSpec("icm_ylt", true, source, mode));
    }
    if (runModelingRater && !raterBlocked) {
      runs.push({
        type: "rater",
        template_override_id: raterTemplateOverride?.template_id ?? null,
        enrichment_source: source,
      });
    }
    if (runModelingEqbd && !raterBlocked) {
      runs.push({ type: "eqbd_rater", enrichment_source: source });
    }
    if (runs.length === 0) return;

    let result: CatRunResult;
    try {
      result = await runModeling.mutateAsync(runs);
    } catch {
      toast.show({
        severity: "error",
        summary: "Modeling Request Failed",
        detail: "There was an error starting your modeling runs.",
      });
      return;
    }
    await Promise.all(result.runs.map(handleRunOutcome));
  };

  // Hands each outcome of a combined request to whatever tracks that run.
  const handleRunOutcome = async (outcome: CatRunResult["runs"][number]) => {
    switch (outcome.type) {
      case "rms_elt":
        return rmsEltPolling.applyOutcome(outcome);
      case "icm_ylt":
        return icmYltPolling.applyOutcome(outcome);
      case "spatialkat":
        return spatialKatPolling.applyOutcome(outcome);
      case "rater":
        return raterPolling.applyOutcome(outcome);
      case "eqbd_rater":
        return eqbdRaterPolling.applyOutcome(outcome);
    }
  };
  const runModelingLoading =
    runModeling.isPending ||
    rmsEltPolling.isInitiateLoading ||
    spatialKatPolling.isInitiateLoading ||
    icmYltPolling.isInitiateLoading ||
    raterPolling.isInitiateLoading ||
    eqbdRaterPolling.isInitiateLoading;
  const runModelingDisabled =
    !runModelingRms &&
    !runModelingKatRisk &&
    !runModelingIcm &&
    !runModelingRater &&
    !runModelingEqbd;
  // Shared by the ENG SplitButton and the plain Button run controls.
  const runButtonProps = {
    label: "Run",
    icon: "pi pi-play",
    onClick: () => handleRunModeling(),
    loading: runModelingLoading,
    disabled: runModelingDisabled,
  };

  const layerInfoById = useMemo(
    () =>
      new Map(
        specialtyPropertyInfo.report_json.layer_info.map((layer) => [
          layer.layer_id!,
          layer,
        ])
      ),
    [specialtyPropertyInfo]
  );

  // The audit source for the current model, or null for models without a
  // financial-model audit (the rater, EB rater and replacement cost tabs).
  // When null, run comparison is unavailable.
  const compareAuditSource =
    selectedModel === "rater" ||
    selectedModel === "eqbd" ||
    selectedModel === "sindri"
      ? null
      : selectedModelToAuditSource(selectedModel);

  // The pool a comparison draws from.
  const compareCandidates = useMemo(
    () =>
      jobHistory.filter(
        (job) => job.type === selectedModel && job.status === WorkStatus.DONE
      ),
    [jobHistory, selectedModel]
  );

  const hasResultsFor = (vendor: SelectedModel) =>
    (resultsByVendor[vendor]?.[vendor]?.aggregate?.layers?.length ?? 0) > 0;
  const hasRmsEltResults = hasResultsFor("rms_elt");
  const hasIcmYltResults = hasResultsFor("icm_ylt");
  const hasSpatialKatResults = hasResultsFor("spatialkat");
  const hasResults = CAT_VENDORS.some(hasResultsFor);
  const selectedVendor: SelectedModel | null =
    selectedModel === "rater" ||
    selectedModel === "eqbd" ||
    selectedModel === "sindri"
      ? null
      : selectedModel;
  // The history batch the selected vendor's tabs show, if not its active run.
  const viewedBatchId =
    selectedVendor === null ? null : viewedBatchByVendor[selectedVendor];
  // The history row of the run the selected vendor's tabs show: the viewed
  // batch, else the active run. Null when the history has no completed row
  // for it (e.g. the moment after a history refresh). Resolved here once and
  // handed down so the picker, table and banner never disagree.
  const viewedJob =
    selectedVendor === null
      ? null
      : (jobHistory.find(
          (job) =>
            job.type === selectedVendor &&
            job.status === WorkStatus.DONE &&
            (viewedBatchId === null
              ? job.is_active
              : job.batch_id === viewedBatchId)
        ) ?? null);
  // What the selected vendor's tab shows: the viewed history batch's vendor
  // data, else its live run's; undefined until known.
  const tabEnrichmentSource: EnrichmentSource | undefined =
    selectedVendor === null
      ? undefined
      : viewedBatchId !== null
        ? viewedJob
          ? jobEnrichmentSource(viewedJob)
          : undefined
        : liveVendor(selectedVendor)?.has_live_run
          ? liveSourceFor(selectedVendor)
          : undefined;

  // Show a completed run on its vendor's tabs. The active run is loaded by
  // the default route so the tab reads as live again, not as pinned history.
  const handleViewJobResults = async (job: CatJobHistoryItem) => {
    // The backend can still report rms_22 jobs, but the tab has no view for them.
    if (job.type === "rms_22") return;
    try {
      const results = await catResults(
        specialtyPropertyInfo.report_id,
        job.is_active ? undefined : job.batch_id
      );
      setVendorResults(job.type, results);
      setViewedBatch(job.type, job.is_active ? null : job.batch_id);
    } catch (error) {
      toast.show({
        severity: "error",
        summary: "Load Failed",
        detail: "Could not load results for this job.",
      });
    }
  };

  // Return one vendor's tabs to its active run.
  const handleViewActiveResults = async (vendor: SelectedModel) => {
    try {
      setVendorResults(
        vendor,
        await catResults(specialtyPropertyInfo.report_id)
      );
      setViewedBatch(vendor, null);
    } catch (error) {
      toast.show({
        severity: "error",
        summary: "Load Failed",
        detail: "Could not load the active results.",
      });
    }
  };

  const handleResetToDefaultResults = async () => {
    try {
      const results = await catResults(specialtyPropertyInfo.report_id);
      setResultsByVendor({
        rms_elt: results,
        icm_ylt: results,
        spatialkat: results,
      });
      setViewedBatchByVendor({
        rms_elt: null,
        icm_ylt: null,
        spatialkat: null,
      });
    } catch (error) {
      toast.show({
        severity: "error",
        summary: "Load Failed",
        detail: "Could not load default results.",
      });
    }
  };

  // Adopt an already-completed run as this model's live results. The activated
  // batch is what the default view resolves to afterward, so the results reset
  // to default instead of staying pinned to whichever row was being inspected.
  const handleActivateRun = async (batchId: string, model: SelectedModel) => {
    setActivatingBatchId(batchId);
    try {
      await activateCatBatch(reportId, model, batchId);
    } catch (error) {
      toast.show({
        severity: "error",
        summary: "Activation Failed",
        // The server's reason separates "a run is in flight" (retry once it
        // finishes) from "this run has no stored results" (retrying won't help).
        detail: apiErrorDetail(error) ?? "Could not make this run active.",
      });
      setActivatingBatchId(null);
      return;
    }
    // The flip has committed. Everything below only refreshes the view, so a
    // failure there must not report the activation itself as failed.
    toast.show({
      severity: "success",
      summary: "Run Activated",
      detail:
        "This run now backs the report's results; staleness re-checks against it.",
    });
    try {
      const history = await JOB_HISTORY_FETCHER[model](reportId);
      handleJobHistoryUpdate(model, history.jobs ?? []);
      await handleResetToDefaultResults();
      refetchPrecheck();
      refreshVendorEnrichment();
    } catch {
      toast.show({
        severity: "warn",
        summary: "View May Be Stale",
        detail: "The run was activated. Reload to see the updated results.",
      });
    } finally {
      setActivatingBatchId(null);
    }
  };

  const handleDownloadAll = async (job: CatJobHistoryItem) => {
    try {
      // Each cat source has its own GCS prefix, so the artifact download is
      // dispatched per type — a mismatched type would look up the wrong
      // prefix (silent empty zip) or name the file misleadingly.
      const downloadFnByType: Partial<
        Record<
          CatJobHistoryItem["type"],
          (
            reportId: string,
            batchId: string
          ) => Promise<{ data: Blob; filename?: string }>
        >
      > = {
        rms_elt: downloadAllRmsEltArtifacts,
        icm_ylt: downloadAllIcmYltArtifacts,
        spatialkat: downloadAllSpatialKatArtifacts,
      };
      const downloadFn = downloadFnByType[job.type];
      if (!downloadFn) {
        return;
      }
      const { data, filename } = await downloadFn(
        specialtyPropertyInfo.report_id,
        job.batch_id
      );
      downloadBlob(filename ?? `${job.type}_all_${job.batch_id}.zip`, data);
    } catch (error) {
      toast.show({
        severity: "error",
        summary: "Download Failed",
        detail: "Could not download the artifacts zip.",
      });
    }
  };

  // Ordered as the "in progress" message lists them.
  const runsInProgress = [
    { pending: rmsEltPolling.isPending, label: "RMS" },
    { pending: spatialKatPolling.isPending, label: "KatRisk" },
    { pending: icmYltPolling.isPending, label: "ICM" },
    { pending: raterPending, label: "Rater" },
    { pending: eqbdRaterPending, label: "EB Rater" },
  ];
  const isPending = runsInProgress.some((run) => run.pending);

  const pendingMessage = (() => {
    const active = runsInProgress
      .filter((run) => run.pending)
      .map((run) => run.label);
    if (active.length === 0) return "";
    const list =
      active.length === 1
        ? active[0]
        : active.length === 2
          ? `${active[0]} and ${active[1]}`
          : `${active.slice(0, -1).join(", ")}, and ${active[active.length - 1]}`;
    return `${list} in progress...`;
  })();

  // AAL drivers are an ICM/RMS output; SpatialKat and the rater tabs have none,
  // and CatResultsView gates the tab on the same three models.
  const topLocationsByPeril =
    selectedModel === "rms_elt" || selectedModel === "icm_ylt"
      ? resultsByVendor[selectedModel]?.[selectedModel]?.top_locations_by_peril
      : undefined;

  return (
    <Card
      title={
        <div className={styles.titleRow}>
          <span className={styles.cardSectionTitle}>Modeling</span>
          <Button
            label={
              hasRmsEltResults ||
              hasSpatialKatResults ||
              hasIcmYltResults ||
              hasRaterResults ||
              hasEqbdResults
                ? "Re-run modeling"
                : "Run modeling"
            }
            icon="pi pi-play"
            raised
            text
            onClick={openRunModelingDialog}
            loading={isPending}
            disabled={runModelingBlocked}
          />
          {precheck &&
            selectedModel !== "rater" &&
            selectedModel !== "eqbd" &&
            selectedModel !== "sindri" && (
              <CatPrecheckBanner
                precheck={precheck}
                selectedModel={selectedModel}
                selectedModelBlocked={selectedModelBlocked}
              />
            )}
          {isMobile && (hasResults || jobHistory.length > 0) && (
            <div className={styles.headerLayerToggle}>
              <Checkbox
                inputId="show-all-layers-mobile"
                checked={showAllLayers}
                onChange={(e) => setShowAllLayers(!!e.checked)}
              />
              <label htmlFor="show-all-layers-mobile">Show all layers</label>
              {tabEnrichmentSource !== undefined && (
                <EnrichmentBadge source={tabEnrichmentSource} />
              )}
            </div>
          )}
        </div>
      }
      className={styles.catCard}
    >
      <div className={styles.content}>
        {blockedModelsRows.length > 0 && (
          <CatStatusBanner
            severity={runModelingBlocked ? "error" : "warn"}
            title="Modeling Blocked"
            rows={blockedModelsRows}
          />
        )}
        {isPending && (
          <Message
            severity="info"
            className={styles.banner}
            content={
              <div className={styles.bannerContent}>
                <ProgressSpinner
                  className={styles.spinnerSmall}
                  strokeWidth="4"
                />
                <span>{pendingMessage}</span>
              </div>
            }
          />
        )}

        {selectedVendor !== null && viewedBatchId !== null && (
          <Message
            severity="warn"
            className={styles.banner}
            content={
              <div className={styles.bannerContent}>
                <span>Viewing a historical run, not the active results.</span>
                <Button
                  label="View active"
                  text
                  size="small"
                  onClick={() => handleViewActiveResults(selectedVendor)}
                />
              </div>
            }
          />
        )}

        {precheck &&
          catStalenessUiEnabled &&
          selectedModel !== "rater" &&
          selectedModel !== "eqbd" &&
          selectedModel !== "sindri" && (
            <CatRunStatusIndicator
              result={
                precheck[VENDOR_PRECHECK_KEY[selectedModel]].results_change
              }
            />
          )}

        <div className={styles.modelSelectionRow}>
          <CatModelSwitcher
            selectedModel={selectedModel}
            onChange={setSelectedModel}
            isMobile={isMobile}
            statusByModel={runStatusByModel}
            showReplacementCost={sindriRunAllowed}
          />
          {selectedModel === "sindri" ? null : selectedModel === "rater" ? (
            <>
              <Button
                label={
                  raterTemplateOverride
                    ? `Generate rater (${raterTemplateOverride.template_name})`
                    : hasRaterResults
                      ? "Re-run rater"
                      : "Generate rater"
                }
                icon="pi pi-play"
                raised
                text
                severity="secondary"
                onClick={() => raterPolling.handleInitiate(true)}
                loading={raterPolling.isInitiateLoading}
                disabled={raterPending || raterBlocked || liveRunsUnknown}
              />
              <TemplateButton
                useCase="rater"
                suffix="rater"
                onOverrideTemplateVersion={setRaterTemplateOverride}
              />
            </>
          ) : selectedModel === "eqbd" ? (
            <Button
              label={hasEqbdResults ? "Re-run EB rater" : "Generate EB rater"}
              icon="pi pi-play"
              raised
              text
              severity="secondary"
              onClick={() => eqbdRaterPolling.handleInitiate(true)}
              loading={eqbdRaterPolling.isInitiateLoading}
              disabled={eqbdRaterPending || raterBlocked || liveRunsUnknown}
            />
          ) : selectedModel === "icm_ylt" ? (
            <>
              <Button
                label={hasIcmYltResults ? "Re-run ICM" : "Run ICM"}
                icon="pi pi-play"
                raised
                text
                severity="secondary"
                onClick={() => icmYltPolling.handleInitiate(true)}
                loading={icmYltPolling.isInitiateLoading}
                disabled={
                  icmYltPolling.isPending ||
                  liveRunsUnknown ||
                  !precheck ||
                  precheck.icm.blocked
                }
              />
              {catShadowRunAllowed && (
                <ShadowRunButton
                  onClick={() => icmYltPolling.handleInitiate(false)}
                  loading={icmYltPolling.isInitiateLoading}
                  disabled={
                    icmYltPolling.isPending ||
                    liveRunsUnknown ||
                    !precheck ||
                    precheck.icm.blocked
                  }
                />
              )}
            </>
          ) : selectedModel === "rms_elt" ? (
            <>
              <Button
                label={hasRmsEltResults ? "Re-run RMS" : "Run RMS"}
                icon="pi pi-play"
                onClick={() =>
                  guardLargeJob(() => rmsEltPolling.handleInitiate(true))
                }
                raised
                text
                severity="secondary"
                loading={rmsEltPolling.isInitiateLoading}
                disabled={
                  rmsEltPolling.isPending ||
                  liveRunsUnknown ||
                  !precheck ||
                  precheck.rms.blocked ||
                  (!catModelOverrideAllowed && rmsNeedsConfirmation)
                }
              />
              {catShadowRunAllowed && (
                <ShadowRunButton
                  onClick={() =>
                    guardLargeJob(() => rmsEltPolling.handleInitiate(false))
                  }
                  loading={rmsEltPolling.isInitiateLoading}
                  disabled={
                    rmsEltPolling.isPending ||
                    liveRunsUnknown ||
                    !precheck ||
                    precheck.rms.blocked ||
                    (!catModelOverrideAllowed && rmsNeedsConfirmation)
                  }
                />
              )}
            </>
          ) : (
            <>
              <Button
                label={
                  hasSpatialKatResults
                    ? "Re-run KatRisk Flood"
                    : "Run KatRisk Flood"
                }
                icon="pi pi-play"
                raised
                text
                severity="secondary"
                onClick={() => spatialKatPolling.handleInitiate(true)}
                loading={spatialKatPolling.isInitiateLoading}
                disabled={
                  spatialKatPolling.isPending ||
                  liveRunsUnknown ||
                  !precheck ||
                  precheck.spatialkat.blocked
                }
              />
              {catShadowRunAllowed && (
                <ShadowRunButton
                  onClick={() => spatialKatPolling.handleInitiate(false)}
                  loading={spatialKatPolling.isInitiateLoading}
                  disabled={
                    spatialKatPolling.isPending ||
                    liveRunsUnknown ||
                    !precheck ||
                    precheck.spatialkat.blocked
                  }
                />
              )}
            </>
          )}
        </div>
        {selectedModel === "rater" ? (
          <RaterTab reportId={reportId} />
        ) : selectedModel === "eqbd" ? (
          <EqbdRaterTab reportId={reportId} />
        ) : selectedModel === "sindri" ? (
          <SindriReplacementCostTab reportId={reportId} />
        ) : hasResults || jobHistory.length > 0 ? (
          <CatResultsView
            isMobile={isMobile}
            selectedModel={selectedModel}
            resultsByVendor={resultsByVendor}
            layerInfoById={layerInfoById}
            showAllLayers={showAllLayers}
            onShowAllLayersChange={setShowAllLayers}
            topLocationsByPeril={topLocationsByPeril}
            reportId={reportId}
            jobHistory={jobHistory}
            viewedBatchId={viewedBatchId}
            viewedJob={viewedJob}
            compareBatchIds={compareBatchesByVendor[selectedModel]}
            onCompareBatchIdsChange={(batchIds) =>
              setCompareBatchesByVendor((prev) => ({
                ...prev,
                [selectedModel]: batchIds,
              }))
            }
            enrichmentSource={tabEnrichmentSource}
            onViewJobResults={handleViewJobResults}
            onDownloadAll={handleDownloadAll}
            onCompareJob={compareAuditSource ? setCompareBaseJob : undefined}
            eltRecovery={eltRecovery}
            activation={
              catActivateRunAllowed && activationModel
                ? {
                    onActivate: (batchId: string) =>
                      handleActivateRun(batchId, activationModel),
                    inFlightBatchId: activatingBatchId,
                  }
                : null
            }
          />
        ) : isInitialLoading ? (
          <div className={styles.loadingContainer}>
            <ProgressSpinner />
          </div>
        ) : !isPending ? (
          <Message
            severity="info"
            text="No CAT results available yet. Run an analysis to generate results."
            className={styles.messageMargin}
          />
        ) : null}
      </div>

      {compareAuditSource && (
        <ModelRunComparisonDialog
          reportId={reportId}
          source={compareAuditSource}
          baseJob={compareBaseJob}
          candidates={compareCandidates}
          layerInfoById={layerInfoById}
          onHide={() => setCompareBaseJob(null)}
        />
      )}

      <Dialog
        header="Run Modeling"
        visible={showRunModelingDialog}
        onHide={() => setShowRunModelingDialog(false)}
        draggable={false}
        footer={
          <div>
            <Button
              label="Cancel"
              icon="pi pi-times"
              severity="secondary"
              text
              onClick={() => setShowRunModelingDialog(false)}
            />
            {catRunModeOverrideAllowed ? (
              // ENG escape hatch: primary Run uses smart staleness; the menu
              // forces a full remodel or a finance-only rerun on the
              // ICM/RMS/SpatialKat baseline runs.
              <SplitButton
                {...runButtonProps}
                outlined
                severity="secondary"
                className={styles.runSplitButton}
                model={[
                  {
                    label: "Run — full remodel",
                    icon: "pi pi-refresh",
                    command: () => handleRunModeling("full"),
                  },
                  {
                    label: "Run — finance-only",
                    icon: "pi pi-dollar",
                    command: () => handleRunModeling("finance"),
                  },
                ]}
              />
            ) : (
              <Button {...runButtonProps} />
            )}
          </div>
        }
      >
        <div className={styles.runModelingOptions}>
          <div className={styles.runModelingOption}>
            <label htmlFor="run-modeling-vendor-data">Vendor data</label>
            <Dropdown
              inputId="run-modeling-vendor-data"
              className={styles.vendorDataDropdown}
              options={ENRICHMENT_SOURCE_OPTIONS}
              optionLabel="label"
              optionValue="value"
              value={runModelingEnrichment}
              onChange={(e) => setRunModelingEnrichment(e.value)}
              tooltip="Models the checked vendors on the broker SOV as-is, or with roof data from Nearmap or Cape plus HazardHub. Each finished run becomes its vendor's live results; switch between stored runs from the Layers tab."
              tooltipOptions={{ position: "top", showDelay: 300 }}
            />
          </div>
          <div>
            <div className={styles.runModelingOption}>
              <Checkbox
                inputId="run-modeling-rms"
                checked={runModelingRms}
                onChange={(e) => setRunModelingRms(!!e.checked)}
                disabled={rmsNotRunnable}
              />
              <label htmlFor="run-modeling-rms">RMS</label>
            </div>
            {precheck && <PerilList vendor={precheck.rms} />}
          </div>
          <div>
            <div className={styles.runModelingOption}>
              <Checkbox
                inputId="run-modeling-katrisk"
                checked={runModelingKatRisk}
                onChange={(e) => setRunModelingKatRisk(!!e.checked)}
                disabled={spatialKatBlocked}
              />
              <label htmlFor="run-modeling-katrisk">KatRisk Flood</label>
            </div>
            {precheck && <PerilList vendor={precheck.spatialkat} />}
          </div>
          <div>
            <div className={styles.runModelingOption}>
              <Checkbox
                inputId="run-modeling-icm"
                checked={runModelingIcm}
                onChange={(e) => setRunModelingIcm(!!e.checked)}
                disabled={icmYltBlocked}
              />
              <label htmlFor="run-modeling-icm">ICM</label>
            </div>
            {precheck && <PerilList vendor={precheck.icm} />}
          </div>
          <div className={styles.runModelingOption}>
            <Checkbox
              inputId="run-modeling-rater"
              checked={runModelingRater}
              onChange={(e) => setRunModelingRater(!!e.checked)}
              disabled={raterBlocked}
            />
            <label htmlFor="run-modeling-rater">
              Fire Rater
              {raterTemplateOverride
                ? ` (${raterTemplateOverride.template_name})`
                : ""}
            </label>
          </div>
          <div className={styles.runModelingOption}>
            <Checkbox
              inputId="run-modeling-eqbd"
              checked={runModelingEqbd}
              onChange={(e) => setRunModelingEqbd(!!e.checked)}
              disabled={raterBlocked}
            />
            <label htmlFor="run-modeling-eqbd">EB Rater</label>
          </div>
        </div>
      </Dialog>

      <Dialog
        header="Large Submission"
        visible={showLargeJobBlockedDialog}
        onHide={() => setShowLargeJobBlockedDialog(false)}
        draggable={false}
        footer={
          <Button
            label="OK"
            onClick={() => setShowLargeJobBlockedDialog(false)}
          />
        }
      >
        <p>
          This submission has <b>{numLocations.toLocaleString()}</b> locations,
          which exceeds the {LARGE_CAT_JOB_THRESHOLD.toLocaleString()} location
          limit for running CAT jobs. Please ping John, Sean, or Andrew to run
          this for you.
        </p>
      </Dialog>
    </Card>
  );
};

const VENDOR_PRECHECK_KEY: Record<SelectedModel, "icm" | "rms" | "spatialkat"> =
  {
    icm_ylt: "icm",
    rms_elt: "rms",
    spatialkat: "spatialkat",
  };

const ShadowRunButton: React.FC<{
  onClick: () => void;
  loading: boolean;
  disabled: boolean;
}> = ({ onClick, loading, disabled }) => (
  <Button
    label="Shadow run"
    icon="pi pi-clone"
    tooltip="Runs the model without replacing the active results — inspect the batch in Run History."
    tooltipOptions={{ position: "bottom" }}
    outlined
    severity="secondary"
    onClick={onClick}
    loading={loading}
    disabled={disabled}
  />
);

const PerilList: React.FC<{ vendor: VendorPrecheck }> = ({ vendor }) => {
  const items = [
    ...vendor.run_perils.map((p) => ({ peril: p, run: true })),
    ...vendor.skipped_perils.map((p) => ({ peril: p, run: false })),
  ].sort((a, b) =>
    getPerilDisplayName(a.peril).localeCompare(getPerilDisplayName(b.peril))
  );
  if (items.length === 0) return null;
  return (
    <div className={styles.perilList}>
      {items.map(({ peril, run }) => (
        <div
          key={peril}
          className={`${styles.perilItem} ${
            run ? styles.perilItemRun : styles.perilItemSkip
          }`}
        >
          <i
            className={`pi ${run ? "pi-check" : "pi-times"} ${
              styles.perilItemIcon
            }`}
          />
          <span>{getPerilDisplayName(peril)}</span>
        </div>
      ))}
    </div>
  );
};

const CatPrecheckBanner: React.FC<{
  precheck: CatPrecheckResponse;
  selectedModel: SelectedModel;
  selectedModelBlocked: boolean;
}> = ({ precheck, selectedModel, selectedModelBlocked }) => {
  // Blocking reasons are in the blocked-models banner, so only advisories here.
  const warnings = [
    ...precheck.warnings.filter((w) => !precheck.blocking_reasons.includes(w)),
    ...(selectedModelBlocked
      ? []
      : precheck[VENDOR_PRECHECK_KEY[selectedModel]].warnings),
  ];
  if (!warnings.length) return null;

  return (
    <Message
      severity="warn"
      className={styles.precheckMessage}
      content={<span>{warnings.join(" ")}</span>}
    />
  );
};
